import { BaseController } from "./base_controller";
import { nanoid } from "nanoid";

window.pagefind = require("../pagefind.js");

export default class extends BaseController {
  static targets = [
    "container",
    "empty",
    "placeholder",
    "searching",
    "count",
    "prev",
    "next",
    "close",
    "label",
    "input",
    "navbarFilter",
    "navbarFilterContainer",
    "itemFilterTemplate",
    "itemFilter",
    "sinLocalizacionContainer",
    "sinLocalizacionTotal",
    "sinLocalizacionIntervencion",
    "sinLocalizacionAcontecimiento",
  ];
  static values = {
    q: String,
    id: String,
    count: Number,
    labels: Object,
    values: Object,
    busquedaUrl: String,
  };

  countValueChanged(value) {
    if (!this.hasCountTarget) return;
    if (value === undefined) return;

    this.countTarget.innerHTML = value;
  }

  relativeUrl(url) {
    return url.toString().replace(window.location.origin, "");
  }

  active(event) {
    for (const labelTarget of this.labelTargets) {
      const labelId = event.target.getAttribute("aria-labelledby");
      const labelStatus = labelTarget.id === labelId;

      for (const item of labelTarget.querySelectorAll(
        "[data-active-add-class],[data-active-remove-class]"
      )) {
        if (labelStatus) {
          if ("activeRemoveClass" in item.dataset)
            item.classList.remove(...item.dataset.activeRemoveClass.split(" "));
          if ("activeAddClass" in item.dataset)
            item.classList.add(...item.dataset.activeAddClass.split(" "));
        } else {
          if ("activeRemoveClass" in item.dataset)
            item.classList.add(...item.dataset.activeRemoveClass.split(" "));
          if ("activeAddClass" in item.dataset)
            item.classList.remove(...item.dataset.activeAddClass.split(" "));
        }
      }
    }
  }

  toggleLabelClass(event) {
    for (const labelTarget of this.labelTargets) {
      const labelId = event.target.dataset.labelId;

      if (labelTarget.id !== labelId) continue;

      const labelInputs = [];

      for (const inputTarget of this.inputTargets) {
        if (inputTarget.dataset.labelId !== labelId) continue;

        labelInputs.push(inputTarget);
      }

      const labelStatus = labelInputs.some((x) => {
        switch (x.type) {
          case "checkbox":
          case "radio":
            return x.checked;
          default:
            return x.value.trim().length;
        }
      });

      for (const item of labelTarget.querySelectorAll(
        "[data-label-add-class],[data-label-remove-class]"
      )) {
        if (labelStatus) {
          if ("labelRemoveClass" in item.dataset)
            item.classList.remove(...item.dataset.labelRemoveClass.split(" "));
          if ("labelAddClass" in item.dataset)
            item.classList.add(...item.dataset.labelAddClass.split(" "));
        } else {
          if ("labelRemoveClass" in item.dataset)
            item.classList.add(...item.dataset.labelRemoveClass.split(" "));
          if ("labelAddClass" in item.dataset)
            item.classList.remove(...item.dataset.labelAddClass.split(" "));
        }
      }
    }
  }

  async connect() {
    this.idValue = window.location.hash;

    const initialSearchParams = new URLSearchParams(window.location.search);
    const willAutoSearch =
      initialSearchParams.has("q") || initialSearchParams.has("sin_localizacion");

    if (willAutoSearch) {
      if (this.hasContainerTarget) this.containerTarget.innerHTML = "";
      this.searchingTargets.forEach((x) => x.classList.remove("d-none"));
    }

    if (this.idValue) {
      const data = JSON.parse(window.localStorage[this.idValue] || "{}");

      if (typeof data.count === "number") this.countValue = data.count;

      // Encontrar la posición del resultado en los resultados actuales.
      const pos = data?.resultUrls?.indexOf(
        decodeURIComponent(window.location.pathname)
      );
      const url = new URL(window.location.origin);

      url.hash = this.idValue;

      // Volver al buscador trayéndolo desde la caché, para que el
      // formulario de búsqueda y resultados quedan llenos.
      if (this.hasCloseTarget) {
        const searchUrl = new URL(data.searchUrl || window.location.origin);

        this.closeTarget.setAttribute("href", this.relativeUrl(searchUrl));
        this.closeTarget.dataset.turboAction = "restore";
      }

      if (pos !== undefined) {
        if (this.hasNextTarget) {
          const nextUrl = data.resultUrls[pos + 1];

          if (nextUrl) {
            url.pathname = nextUrl;
            this.nextTarget
              .querySelector("a[href]")
              ?.setAttribute("href", this.relativeUrl(url));
          } else {
            this.nextTarget.setAttribute("hidden", true);
            this.nextTarget.classList.add("invisible");
          }
        }

        if (this.hasPrevTarget) {
          const prevUrl = data.resultUrls[pos - 1];

          if (prevUrl) {
            url.pathname = prevUrl;
            this.prevTarget
              .querySelector("a[href]")
              ?.setAttribute("href", this.relativeUrl(url));
          } else {
            this.prevTarget.setAttribute("hidden", true);
            this.prevTarget.classList.add("invisible");
          }
        }
      } else {
        this.nextTarget.setAttribute("hidden", true);
        this.prevTarget.setAttribute("hidden", true);
        this.nextTarget.classList.add("invisible");
        this.prevTarget.classList.add("invisible");
      }
    }

    await window.pagefind.options({
      baseUrl: "",
      bundlePath: "pagefind/",
      excerptLength: 0,
    });

    await window.pagefind.init();
    this.filters = await window.pagefind.filters();

    if (window.location.search) {
      const searchParams = initialSearchParams;

      if (searchParams.has("q")) {
        const form = this.element.querySelector("form");

        if ("q" in form.elements) {
          form.elements.q.value = searchParams.get("q");
          this.search({ target: form });
        }
      } else if (searchParams.has("sin_localizacion")) {
        let q = null;
        let filters = {};

        const searchId = searchParams.get("search_id");
        if (searchId) {
          try {
            const data = JSON.parse(window.localStorage.getItem(searchId) || "{}");
            q = data.q || null;
            filters = data.filters || {};
          } catch (e) {
            console.error(e);
          }
        }

        await this.mostrarSinLocalizacion(q, filters);
      }
    }
  }

  explorarSinLocalizacion() {
    const params = new URLSearchParams({ sin_localizacion: 1 });
    if (this.idValue) params.set("search_id", this.idValue);
    window.location.href = `${this.busquedaUrlValue}?${params.toString()}`;
  }

  async mostrarSinLocalizacion(q = null, filters = {}) {
    if (!this.hasContainerTarget) return;

    this.searchingTargets.forEach((x) => x.classList.remove("d-none"));

    const allResults = await window.pagefind.search(q, { filters });
    const resolved = await this.resolveResults(allResults.results);
    const sinGeo = resolved.filter((x) => !x.meta?.geo_json);

    this.countValue = sinGeo.length;
    this.containerTarget.innerHTML = "";
    this.searchingTargets.forEach((x) => x.classList.add("d-none"));

    if (sinGeo.length) {
      this.idValue = `#${nanoid()}`;
      const resultUrls = sinGeo.map((x) => x.url.replace("/indexed", ""));

      window.localStorage.setItem(
        this.idValue,
        JSON.stringify({
          searchUrl: window.location.href,
          count: sinGeo.length,
          q,
          filters,
          resultUrls,
          items: {},
        })
      );

      this.renderResults(sinGeo);
    } else {
      this.emptyTargets.forEach((x) => x.classList.remove("d-none"));
    }
  }

  async search(event) {
    const form = event.target.closest('form') ?? event.target;
    this.lastSearchForm = form;
    const formData = new FormData(form);
    const formDataSerialized = new URLSearchParams(formData).toString();
    const filters = {};
    const items = {};
    const q = formData.get("q").trim() || null;

    for (const key of formData.keys()) {
      if (key === "q") {
        if (q)
          items.q = { titleField: this.labelsValue.q, title: q, filter: "q" };
        continue;
      } else if (key.endsWith("_min")) {
        const minRaw = formData.get(key);
        const maxRaw = formData.get(key.replace("_min", "_max"));

        if (!minRaw.trim() && !maxRaw.trim()) continue;

        const rangeMin = parseInt(minRaw) || 0;
        const rangeMax = parseInt(maxRaw) || rangeMin;

        const minEl = form.elements[key];
        const maxEl = form.elements[key.replace("_min", "_max")];
        if (
          minEl &&
          maxEl &&
          rangeMin === parseInt(minEl.min) &&
          rangeMax === parseInt(maxEl.max)
        )
          continue;

        const any = Array.from(
          { length: rangeMax - rangeMin + 1 },
          (_, k) => k + rangeMin
        ).map((x) => x.toString());

        filters[key.replace("_min", "")] = { any };

        items[key] = {
          titleField: this.labelsValue[key],
          title: `${any[0]}-${any[any.length - 1]}`,
          filter: key,
        };
      } else if (key.endsWith("_max")) {
        continue;
      } else if (!this.filters[key]) {
        console.error(`El filtro ${key} no está configurado en Pagefind`);
        continue;
      } else {
        const any = formData.getAll(key);

        if (any.length === 0) continue;

        filters[key] = { any };

        for (const value of any) {
          items[`${key}-${value}`] = {
            titleField: this.labelsValue[key] || key,
            value,
            title: this.valuesValue[value] || value,
            filter: key,
          };
        }
      }
    }

    if (this.hasNavbarFilterContainerTarget) {
      this.navbarFilterContainerTarget.classList[
        this.isEmpty(items) ? "add" : "remove"
      ]("d-none");
      this.itemFilterTargets.forEach((x) => x.remove());

      for (const item in items) {
        this.navbarFilterTarget.appendChild(
          this.applyTemplate({ ...items[item] }, this.itemFilterTemplateTarget)
        );
      }
    }

    this.qValue = q;
    this.formDisable(form, true);
    this.searchingTargets.forEach((x) => x.classList.remove("d-none"));

    // TODO: FormData como objeto
    this.searchResults = await window.pagefind.search(q, { filters });
    const count = this.searchResults.results.length;

    if (this.hasContainerTarget) this.containerTarget.innerHTML = "";
    this.countValue = count;
    this.emptyTargets.forEach((x) => x.classList.add("d-none"));
    this.searchingTargets.forEach((x) => x.classList.add("d-none"));

    if (count) {
      this.idValue = `#${nanoid()}`;

      const results = await this.resolveResults(this.searchResults.results);
      const resultUrls = results.map((x) => x.url.replace("/indexed", ""));
      const searchUrl = window.location.href;

      // Mientras separamos el nanoid podemos volver acá
      window.localStorage.setItem(
        this.idValue,
        JSON.stringify({
          searchUrl,
          count,
          q,
          filters,
          formDataSerialized,
          resultUrls,
          items,
        })
      );

      try {
        // TODO: Configurable
        const geoJson = await (await fetch("/geo_json/all.geojson")).json();
        const geoJsons = results.map((x) => x.meta?.geo_json).filter((x) => x);

        const featureCollection = {
          type: "FeatureCollection",
          features: geoJson.features.filter((x) =>
            geoJsons.includes(x.properties.feature_collection)
          ),
        };

        this.dispatch("filterMap", { detail: { featureCollection } });
      } catch (e) {
        console.error(e);
      }

      this.updateSinLocalizacion(results);

      if (this.hasContainerTarget) this.renderResults(results);
    } else {
      this.emptyTargets.forEach((x) => x.classList.remove("d-none"));
    }

    this.formDisable(form, false);

    for (const input of form.querySelectorAll('input[type="range"]')) {
      input.dispatchEvent(new Event("input"));
    }
  }

  updateSinLocalizacion(results) {
    if (!this.hasSinLocalizacionContainerTarget) return;

    const sinGeo = results.filter((x) => !x.meta?.geo_json);
    const byLayout = {};
    for (const r of sinGeo) {
      const layout = r.meta?.layout;
      if (layout) byLayout[layout] = (byLayout[layout] || 0) + 1;
    }

    this.sinLocalizacionContainerTarget.classList[sinGeo.length ? "remove" : "add"]("d-none");
    if (this.hasSinLocalizacionTotalTarget)
      this.sinLocalizacionTotalTarget.innerText = sinGeo.length;
    if (this.hasSinLocalizacionIntervencionTarget)
      this.sinLocalizacionIntervencionTarget.innerText = byLayout.intervencion || 0;
    if (this.hasSinLocalizacionAcontecimientoTarget)
      this.sinLocalizacionAcontecimientoTarget.innerText = byLayout.acontecimiento || 0;
  }

  /*
   * Resuelve los datos de todos los resultados
   *
   * @param results [Array<Result>]
   * @return [Promise]
   */
  async resolveResults(results) {
    return await Promise.all(results.map((r) => r.data()));
  }

  async reset(event) {
    this.searchResults = null;

    for (const item of this.element.querySelectorAll(
      "[data-label-add-class],[data-label-remove-class],[data-active-remove-class],[data-active-add-class]"
    )) {
      if ("labelRemoveClass" in item.dataset)
        item.classList.add(...item.dataset.labelRemoveClass.split(" "));
      if ("labelAddClass" in item.dataset)
        item.classList.remove(...item.dataset.labelAddClass.split(" "));
      if ("activeRemoveClass" in item.dataset)
        item.classList.add(...item.dataset.activeRemoveClass.split(" "));
      if ("activeAddClass" in item.dataset)
        item.classList.remove(...item.dataset.activeAddClass.split(" "));
    }
  }

  removeFilter(event) {
    const button = event.currentTarget;
    const filter = button.dataset.pagefindFilterParam;
    const value = button.dataset.pagefindValueParam;
    const form = this.lastSearchForm;

    if (!form) return;

    if (filter === "q") {
      const qInput = form.elements.q;
      if (qInput) {
        qInput.value = "";
        qInput.dispatchEvent(new Event("change", { bubbles: false }));
      }
    } else if (filter.endsWith("_min")) {
      const baseName = filter.replace("_min", "");
      const minInput = form.elements[`${baseName}_min`];
      const maxInput = form.elements[`${baseName}_max`];

      if (minInput) {
        minInput.value = minInput.min;
        minInput.dispatchEvent(new Event("change", { bubbles: false }));
      }
      if (maxInput) {
        maxInput.value = maxInput.max;
        maxInput.dispatchEvent(new Event("change", { bubbles: false }));
      }
    } else {
      for (const input of form.querySelectorAll(`[name="${filter}"]`)) {
        if (input.value === value) {
          input.checked = false;
          input.dispatchEvent(new Event("change", { bubbles: false }));
        }
      }
    }

    this.search({ target: form });
  }

  updateHistory() {
    const q = this.qValue;
    const query = new URLSearchParams({ q });

    window.history.pushState({ q }, q, `?${query.toString()}`);
  }

  formDisable(form, disable) {
    Array.from(form.elements).forEach((x) => (x.disabled = disable));
  }

  renderResults(results) {
    results.forEach((result) => this.renderResult(result));
  }

  renderResult(result) {
    const placeholder =
      this.placeholderTarget.content.firstElementChild.cloneNode(true);

    this.containerTarget.appendChild(placeholder);

    // TODO: Gestión de errores y cancelar peticiones
    fetch(result.url)
      .then((response) => response.text())
      .then((body) => {
        const template = document.createElement("template");
        template.innerHTML = body;
        const result = template.content.firstElementChild;

        result.querySelectorAll("[href]").forEach((x) => {
          let url;

          if (x.href.includes("://")) {
            url = new URL(x.href);
          } else {
            url = new URL(window.location.origin);
            url.pathname = x.href;
          }

          url.hash = this.idValue;

          x.href = this.relativeUrl(url);
        });

        placeholder.replaceWith(result);
      })
      .catch(console.error);
  }
}
